import { Box, ClickAwayListener } from '@mui/material';
import { SearchFieldTrigger } from 'Components/SearchFieldTrigger';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary } from 'Hooks';
import { RefObject } from 'react';
import { sendEvent } from 'Utils/sendEvent';
import { SearchInput } from '../SearchInput';
import { SearchContent } from '../SearchContent';
import { ContentPaper } from '../ContentPaper';
import { IRelatedAction, IResult } from '../hooks/useSearchLogic';

interface SearchDropdownProps {
  isActive: boolean;
  value: string;
  results: IResult[];
  relatedActions: IRelatedAction[];
  isLoading: boolean;
  showNoResults: boolean;
  showResults: boolean;
  hasContent: boolean;
  minSearchLength: number;
  placeholder?: string;
  containerRef: RefObject<HTMLDivElement | null>;
  searchBarRef: RefObject<HTMLButtonElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  onActivate: () => void;
  onClose: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onClear: () => void;
  onActivityClick: (action: IRelatedAction) => void;
}

export const SearchDropdown = ({
  isActive,
  value,
  results,
  relatedActions,
  isLoading,
  showNoResults,
  showResults,
  hasContent,
  minSearchLength,
  placeholder,
  containerRef,
  searchBarRef,
  inputRef,
  onActivate,
  onClose,
  onInputChange,
  onKeyDown,
  onClear,
  onActivityClick,
}: SearchDropdownProps) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.ActivitiesLobby);

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', width: '564px', mt: '24px' }}
    >
      {!isActive ? (
        <SearchFieldTrigger
          triggerRef={searchBarRef}
          label={placeholder || dictionary.searchButton}
          onActivate={() => {
            sendEvent('Search Page', 'HomePageSearch');
            onActivate();
          }}
          height='56px'
          borderRadius='30px'
          sx={{
            width: '564px',
            '&:focus': { outlineColor: 'rgb(21, 82, 232)' },
          }}
          typographySx={{
            fontFamily: 'Assistant',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: 'text.primary',
          }}
        />
      ) : (
        <ClickAwayListener onClickAway={onClose}>
          <Box>
            <SearchInput
              value={value}
              inputRef={inputRef}
              useCurtain={false}
              onInputChange={onInputChange}
              onKeyDown={onKeyDown}
              onClear={onClear}
            />
            {(hasContent || value.length > 0) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '72px',
                  right: 0,
                  zIndex: 99999,
                }}
              >
                <ContentPaper>
                  <SearchContent
                    value={value}
                    results={results}
                    relatedActions={relatedActions}
                    isLoading={isLoading}
                    showNoResults={showNoResults}
                    showResults={showResults}
                    minSearchLength={minSearchLength}
                    onActivityClick={onActivityClick}
                  />
                </ContentPaper>
              </Box>
            )}
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};
